import {
  FinancesVGrubsNetBreakdown,
  FinancesVGrubsProviderRow,
  formatAmount,
  lightTheme,
  NumberType,
  renderProviderIcon,
} from '@vgrubs/components';
import {
  BreakdownSubDetail,
  BreakdownSubItem,
  BreakdownSubTag,
} from './FinanceTabbedCard';
import { financeProviderLabel } from './serviceLegend';

/** The three parts of `net`, in the order they are listed under a row. */
const NET_LINES: { key: keyof FinancesVGrubsNetBreakdown; label: string }[] = [
  { key: 'vGrubsFee', label: 'vGrubs Fee' },
  { key: 'salesTax', label: 'Sales Tax' },
  { key: 'marketplaceFee', label: 'Marketplace Override Fee' },
];

/**
 * What `net` is made of. The API guarantees the three add up to `net`, so the
 * lines are printed as they arrive.
 *
 * Zero parts are dropped: most providers carry no marketplace fee, and a row
 * of "$0.00" under every provider buries the two numbers that do move. An
 * older deployment sends no `netBreakdown` at all - then the row stays the
 * single `net` figure it was before.
 */
const netDetails = (
  row: FinancesVGrubsProviderRow
): BreakdownSubDetail[] | undefined => {
  const breakdown = row.netBreakdown;
  if (!breakdown) return undefined;

  const lines = NET_LINES.filter(({ key }) => breakdown[key] !== 0).map(
    ({ key, label }) => ({ label, amount: breakdown[key] })
  );

  return lines.length ? lines : undefined;
};

/**
 * What the provider actually paid out per its own report.
 *
 * Kept on the Gross card only, and deliberately never next to `net`: the
 * payout carries the location's money too, so the two sitting side by side
 * would invite adding them together. The order count says how much of the
 * range the report covers - orders complete before their report lands, so the
 * two counts rarely match, and the gap is the point of showing it.
 *
 * Nothing at all when no money came in through a report: the provider sends
 * none (DoorDash has no report API), none landed inside the range, or this
 * deployment predates the field. A row of "$0.00" or a dash in those cases
 * says nothing the reader can act on and only makes the list longer.
 */
const reportDetails = (
  row: FinancesVGrubsProviderRow
): BreakdownSubDetail[] | undefined => {
  const payout = row.reportPayout ?? 0;
  const covered = row.reportOrders ?? 0;

  if (!payout && !covered) return undefined;

  return [
    {
      label: covered
        ? `Report Net Payout (${covered} of ${row.orders} orders)`
        : 'Report Net Payout',
      amount: payout,
    },
  ];
};

/**
 * What vGrubs kept of the provider's order volume - `net` over `gross`, as a
 * percentage - shown as a chip right after the provider's name.
 *
 * It is the number the two cards leave the reader to work out by hand: the
 * Gross card says how much went through a provider, the Net card how much of
 * it stayed with vGrubs, and "so what do we make on Uber?" is the ratio of
 * the two. Net card only: it answers that card's question, and next to the
 * order volume on the Gross card it read as a split of the volume itself
 * rather than of what came back. Not to be confused with the share under the
 * amount, which is the row against the other providers.
 *
 * Green when the row earned, red when it lost money - the same signal the
 * amount itself uses. No chip when there is no volume to read it against
 * (gross of zero or below): "0.00%" would say we earned nothing on orders
 * that did not happen. The two columns can cover slightly different order
 * sets inside a range (see the type), so the figure is a rate over the
 * range rather than a per-order guarantee.
 *
 * The tooltip spells out the range and the two amounts behind it: the row
 * prints only `net`, and a bare "18.14%" is a share of something the reader
 * has to guess. The range is already in the modal header, but the tooltip is
 * read with the eyes on one chip, and "in the period" says nothing there.
 */
const takeRateTag = (
  row: FinancesVGrubsProviderRow,
  providerLabel: string,
  periodLabel?: string
): BreakdownSubTag | undefined => {
  if (row.gross <= 0) return undefined;

  const rate = (row.net / row.gross) * 100;
  const negative = rate < 0;

  return {
    label: `${rate.toFixed(2)}%`,
    color: negative ? lightTheme.primary : lightTheme.green,
    background: negative
      ? lightTheme.transparentRed
      : lightTheme.transparentGreen,
    tooltip:
      `${periodPhrase(periodLabel)}, vGrubs kept ` +
      `${formatAmount(row.net, NumberType.USD)} net of ` +
      `${formatAmount(
        row.gross,
        NumberType.USD
      )} gross in ${providerLabel} orders.`,
  };
};

/**
 * "From Sep 01, 2026 to Sep 09, 2026", "On Sep 05, 2026" or "Today", out of
 * the card's period label. `renderDateLabelWithToday` builds that label as
 * either "Today" or "MMM dd, yyyy - MMM dd, yyyy"; it is parsed back here
 * rather than threading the raw dates through the card, since the label is
 * what the card already holds and the dates never contain the " - " it splits
 * on. Anything else (or no label at all) falls back to "In this period".
 */
const periodPhrase = (periodLabel?: string): string => {
  if (!periodLabel) return 'In this period';
  if (periodLabel === 'Today') return 'Today';

  const [from, to] = periodLabel.split(' - ');
  if (!from || !to) return 'In this period';
  if (from === to) return `On ${from}`;

  return `From ${from} to ${to}`;
};

/**
 * Turn `vGrubsProviderBreakdown` into the lines that expand under the
 * "vGrubs VR" legend row.
 *
 * The same list feeds both cards - the Gross pie reads `gross`, the Net pie
 * reads `net` - so there is no second request when a row is opened. Each side
 * carries the supporting lines that belong to its own number: the report
 * payout under gross, the split of the earning under net.
 *
 * A row worth $0.00 on the shown side is kept. The API only sends providers
 * that had traffic in the range, so a zero is a fact about the range, not an
 * empty row: an order can be completed before its payment report lands (or
 * the other way round). Hiding them made the two cards disagree about which
 * providers even exist.
 *
 * @param metric which column this card shows.
 * @param withOrders append the order count. Only true on the Net card: the
 *   count belongs to `net`, not to `gross` (they are different sets of
 *   orders), so hanging it off a gross amount would be a number that does not
 *   describe the money next to it.
 * @param periodLabel the card's date range, as shown under its headline.
 *   Named in the take-rate tooltip on the Net card; the Gross card has no
 *   chip and need not pass it.
 */
export const buildVGrubsProviderRows = (
  rows: FinancesVGrubsProviderRow[] | null | undefined,
  metric: 'gross' | 'net',
  withOrders: boolean,
  periodLabel?: string
): BreakdownSubItem[] =>
  (rows ?? [])
    .map((row) => {
      const label = financeProviderLabel(row.provider);

      return {
        label,
        // `renderProviderIcon` only has art for the app's narrower `Provider`
        // enum; the finance endpoints report the backend's wider `ProviderType`.
        // Left undefined for a marketplace we have no logo for - the row draws
        // its own initial rather than borrowing another brand's mark.
        icon: renderProviderIcon(row.provider),
        amount: row[metric],
        tag:
          metric === 'net' ? takeRateTag(row, label, periodLabel) : undefined,
        note:
          withOrders && row.orders > 0
            ? `${row.orders} ${row.orders === 1 ? 'order' : 'orders'}`
            : undefined,
        details: metric === 'net' ? netDetails(row) : reportDetails(row),
      };
    })
    // The API sorts by `gross`, which leaves the Net card's rows in an order
    // its own numbers do not explain - a provider with no earning booked yet
    // would sit at the top of a list of earnings. Each card ranks by the
    // column it actually shows; negatives fall to the bottom, where they read
    // as the exceptions they are.
    .sort((a, b) => b.amount - a.amount);
